import { normalizeWalletKey } from '@/lib/launchpad/store'

const WATCHLIST_KEY = 'penguin-pad-watchlist'
const WATCHLIST_BY_WALLET_KEY = 'penguin-pad-watchlist-by-wallet'
export const WATCHLIST_EVENT = 'penguin-pad-watchlist-change'

type WatchlistByWallet = Record<string, string[]>

function canUseStorage() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'
}

function readJson<T>(key: string, fallback: T): T {
  if (!canUseStorage())
    return fallback
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw)
      return fallback
    return JSON.parse(raw) as T
  }
  catch {
    return fallback
  }
}

function writeJson(key: string, value: unknown) {
  if (!canUseStorage())
    return
  try {
    window.localStorage.setItem(key, JSON.stringify(value))
    window.dispatchEvent(new Event(WATCHLIST_EVENT))
  }
  catch {
    // Ignore quota / private-mode failures in this mock UI.
  }
}

function migrateWatchlists(): WatchlistByWallet {
  const next = readJson<WatchlistByWallet>(WATCHLIST_BY_WALLET_KEY, {})
  if (next && typeof next === 'object' && !Array.isArray(next))
    return next
  return {}
}

function claimLegacy(store: WatchlistByWallet, walletKey: string): string[] {
  const existing = store[walletKey]
  if (Array.isArray(existing))
    return existing.filter((id): id is string => typeof id === 'string')
  const legacy = readJson<unknown>(WATCHLIST_KEY, [])
  if (Array.isArray(legacy) && legacy.length > 0) {
    const ids = legacy.filter((id): id is string => typeof id === 'string')
    store[walletKey] = ids
    writeJson(WATCHLIST_BY_WALLET_KEY, store)
    if (canUseStorage())
      window.localStorage.removeItem(WATCHLIST_KEY)
    return ids
  }
  return []
}

export function getWatchlist(address?: string): string[] {
  if (!address?.trim())
    return []
  const walletKey = normalizeWalletKey(address)
  return claimLegacy(migrateWatchlists(), walletKey)
}

export function isWatched(projectId: string, address?: string): boolean {
  return getWatchlist(address).includes(projectId)
}

export function toggleWatch(projectId: string, address?: string): boolean {
  if (!address?.trim())
    return false
  const walletKey = normalizeWalletKey(address)
  const store = migrateWatchlists()
  const current = claimLegacy(store, walletKey)
  const next = current.includes(projectId)
    ? current.filter(id => id !== projectId)
    : [projectId, ...current]
  store[walletKey] = next
  writeJson(WATCHLIST_BY_WALLET_KEY, store)
  return next.includes(projectId)
}
